<template>
  <div class="ortic-form-horizontal">
    <div v-if="loading" class="text-center py-5">
      <b-spinner/>
    </div>

    <div v-else-if="loadError" class="alert alert-danger">
      {{ loadError }}
    </div>

    <div v-else>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h3 class="mb-0">{{ title }}</h3>
        <span class="badge badge-pill" :class="statusBadgeClass">
          {{ item.status || $t('sandoz.draft') }}
        </span>
      </div>

      <b-tabs class="inline-tabs" ref="tabs" v-model="activeTab">
        <!-- Always-on header tab: general + plan data + multi-year + bank data -->
        <b-tab :title="$t('sandoz.overview')" active>
          <contract-section name="SectionGeneral" :enabled="true"/>
          <contract-section name="SectionPlanData" :enabled="true"/>
          <contract-section name="SectionMultiYear" :enabled="features.has_multiyear"/>
          <contract-section name="SectionBankData" :enabled="features.has_bank_data"/>
        </b-tab>

        <!-- Items / lines -->
        <b-tab v-if="features.has_items" :title="$t('sandoz.items')">
          <contract-section name="SectionItems" :enabled="true"/>
        </b-tab>

        <!-- KAM-specific -->
        <b-tab v-if="features.has_kam_addresses || features.has_kam_item_proofs" :title="$t('sandoz.kam')">
          <contract-section name="SectionKamAddresses" :enabled="features.has_kam_addresses"/>
          <contract-section name="SectionKamItemProofs" :enabled="features.has_kam_item_proofs"/>
        </b-tab>

        <!-- Educational-specific -->
        <b-tab v-if="features.has_educational" :title="$t('sandoz.educational')">
          <contract-section name="SectionEducational" :enabled="true"/>
        </b-tab>

        <!-- KUKO inline editor -->
        <b-tab v-if="features.has_kuko_conditions" :title="$t('sandoz.kuko_conditions')">
          <contract-section name="SectionKuko" :enabled="true"/>
        </b-tab>

        <!-- Invoicing & verification -->
        <b-tab v-if="features.has_invoicing || features.has_verification" :title="$t('sandoz.invoicing_and_verification')">
          <contract-section name="SectionInvoicing" :enabled="features.has_invoicing"/>
          <contract-section name="SectionVerification" :enabled="features.has_verification"/>
        </b-tab>

        <!-- Documents -->
        <b-tab :title="$t('sandoz.documents')">
          <contract-section name="SectionPdf" :enabled="features.has_pdf_generation"/>
          <contract-section name="SectionFiles" :enabled="true"/>
        </b-tab>

        <!-- Approvals (always visible) -->
        <b-tab :title="$t('sandoz.approvals')">
          <contract-section name="SectionApprovals" :enabled="true"/>
        </b-tab>

        <!-- Debug: resolved features -->
        <b-tab :title="$t('sandoz.features_card_title')" lazy>
          <p class="text-muted small">{{ $t('sandoz.dyn_contract_features_debug_help') }}</p>
          <ul class="list-unstyled mb-0">
            <li v-for="(value, flag) in features" :key="flag">
              <code>{{ flag }}</code>: <strong>{{ value ? '✓' : '–' }}</strong>
            </li>
          </ul>
        </b-tab>
      </b-tabs>

      <div class="text-right mt-3">
        <b-btn variant="primary" @click="save" :disabled="saving || readonly">
          <b-spinner v-if="saving" small/>
          {{ $t('core.save') }}
        </b-btn>
        <small class="d-block text-muted mt-1">{{ $t('sandoz.dyn_contract_save_help') }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ContractSection from './ContractSection.vue'

/**
 * Dynamic contract detail page. Sections are gated by the resolved feature flags on the
 * contract's sales line — the same contract type renders different tab sets without us
 * touching this file.
 *
 * Save goes through the dynamic contract controller, which delegates to the generic
 * Sandoz\ContractController. Cross-cutting side effects (KUKO sync, status sync, etc.) live
 * at the model layer so the legacy detail and this page produce identical persisted state.
 */
export default {
  components: { ContractSection },
  data() {
    return {
      loading: true,
      loadError: null,
      saving: false,
      item: {},
      features: {},
      salesLineId: null,
      activeTab: 0
    }
  },
  provide() {
    return {
      dynamicContract: this
    }
  },
  computed: {
    title() {
      const label = this.$t('sandoz.contract')
      return `${label} // ${this.item.contract_no || '-'}`
    },
    statusBadgeClass() {
      const map = {
        'Draft': 'badge-secondary',
        'Committed': 'badge-info',
        'Approved': 'badge-success',
        'Signed': 'badge-success',
        'Feedback': 'badge-warning',
        'Rejected': 'badge-danger',
        'Cancelled': 'badge-dark'
      }
      return map[this.item.status] || 'badge-secondary'
    },
    readonly() {
      const status = this.item.status || 'Draft'
      return status !== 'Draft' && status !== 'Feedback'
    }
  },
  created() {
    this.bootstrap()
  },
  watch: {
    '$route.params.id'() {
      this.bootstrap()
    }
  },
  methods: {
    async bootstrap() {
      const id = this.$route.params.id
      this.loading = true
      this.loadError = null

      try {
        const [contractRes, featuresRes] = await Promise.all([
          axios.get(`/api/sandoz/dyn/contracts/${id}`),
          axios.get(`/api/sandoz/dyn/contracts/${id}/bootstrap`)
        ])

        this.item = contractRes.data || {}
        this.features = (featuresRes.data && featuresRes.data.features) || {}
        this.salesLineId = featuresRes.data ? featuresRes.data.sales_line_id : null
      } catch (err) {
        this.loadError = (err.response && err.response.data && err.response.data.message) || err.message
      } finally {
        this.loading = false
      }
    },
    async save() {
      this.saving = true
      try {
        const id = this.item.id || this.$route.params.id
        const resp = await axios.post(`/api/sandoz/dyn/contracts/${id}`, this.item)
        this.item = resp.data || this.item
      } catch (err) {
        this.loadError = (err.response && err.response.data && err.response.data.message) || err.message
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
